// client/src/coms/settings/sections/SpeakingAnimationSettings.js
// Personal Settings → Speaking Animations: every animation in the library with a live preview, applied the
// moment you pick it (saved to your account and sent to people in your voice channel), a large preview with
// "Test speaking" (simulated speech, or your real microphone against your sensitivity setting), and how the
// animations behave when reduced motion is on. Previews need no voice connection.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Row, Section, Segmented } from '../../personalisation/settingsControls';
import { usePersonalisation } from '../../personalisation/PersonalisationProvider';
import { AnimationStage, DEFAULT_VOICE_ANIMATION, VOICE_ANIMATION_OPTIONS, VoiceAnimationStyles, getVoiceAnimationLabel, isVoiceAnimationKey } from '../../components/voiceAnimations';
import { notify } from '../../organisation/orgShared';
import useMicMeter from '../useMicMeter';

const LS_VOICE_ANIMATION = 'vox:voiceAnimation';

export function readVoiceAnimationLS() {
  try {
    const raw = String(window.localStorage.getItem(LS_VOICE_ANIMATION) || '').trim();
    return isVoiceAnimationKey(raw) ? raw : DEFAULT_VOICE_ANIMATION;
  } catch {
    return DEFAULT_VOICE_ANIMATION;
  }
}

function writeVoiceAnimationLS(value) {
  try {
    if (!value || value === DEFAULT_VOICE_ANIMATION) window.localStorage.removeItem(LS_VOICE_ANIMATION);
    else window.localStorage.setItem(LS_VOICE_ANIMATION, value);
  } catch {}
}

// The voice client listens for this and tells everyone in the channel (see voip/voiceclient_parts/proximity.js).
function announceVoiceAnimation(value) {
  try {
    window.dispatchEvent(new CustomEvent('vox-voice-animation-changed', { detail: { value } }));
  } catch {}
}

async function apiVoiceAnimation(method, body) {
  const res = await fetch('/api/coms/me/voice-animation', {
    method,
    credentials: 'include',
    cache: 'no-store',
    headers: body ? { Accept: 'application/json', 'Content-Type': 'application/json' } : { Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok || !data || !data.ok) throw new Error((data && data.error) || `Could not reach the server (${res.status}).`);
  const v = String(data.effective_voice_animation || data.active_voice_animation || data.default_voice_animation || DEFAULT_VOICE_ANIMATION).trim();
  return isVoiceAnimationKey(v) ? v : DEFAULT_VOICE_ANIMATION;
}

// A speech-like on/off rhythm (ms) for "Test speaking" without a microphone.
const SPEECH_PATTERN = [900, 260, 1400, 520, 600, 220, 1100, 900];

function useSimulatedSpeech(running) {
  const [active, setActive] = useState(false);
  useEffect(() => {
    if (!running) {
      setActive(false);
      return undefined;
    }
    let i = 0;
    let timer = 0;
    const step = () => {
      setActive(i % 2 === 0);
      timer = setTimeout(step, SPEECH_PATTERN[i % SPEECH_PATTERN.length]);
      i += 1;
    };
    step();
    return () => clearTimeout(timer);
  }, [running]);
  return active;
}

export default function SpeakingAnimationSettings({ gateDb, inputId }) {
  const pz = usePersonalisation();
  const [value, setValue] = useState(() => readVoiceAnimationLS());
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const [test, setTest] = useState(''); // '' | 'sim' | 'mic'
  const [hovered, setHovered] = useState('');
  const latest = useRef(value);
  latest.current = value;

  useEffect(() => {
    let cancelled = false;
    apiVoiceAnimation('GET')
      .then((v) => {
        if (cancelled) return;
        setValue(v);
        writeVoiceAnimationLS(v);
      })
      .catch((e) => !cancelled && setError(`Showing the animation saved on this device: ${e.message}`));
    return () => {
      cancelled = true;
    };
  }, []);

  const choose = useCallback(async (key) => {
    if (!isVoiceAnimationKey(key) || key === latest.current) return;
    const prev = latest.current;
    setValue(key);
    setSaving(key);
    setError('');
    try {
      const saved = await apiVoiceAnimation('PUT', { active_voice_animation: key });
      setValue(saved);
      writeVoiceAnimationLS(saved);
      announceVoiceAnimation(saved);
    } catch (e) {
      setValue(prev);
      setError(`Could not save that animation: ${e.message}`);
      notify('Your speaking animation was not changed.', { tone: 'error' });
    } finally {
      setSaving('');
    }
  }, []);

  const simActive = useSimulatedSpeech(test === 'sim');
  const mic = useMicMeter(test === 'mic', inputId);
  const previewActive = test === 'sim' ? simActive : test === 'mic' ? mic.db >= gateDb : true;

  const still = pz.speakingStill;
  const motionNote = pz.reduceMotion
    ? `Reduced motion is on${pz.globalPrefs.theme.reduceMotion === 'system' ? ' (from your device settings)' : ''}.`
    : '';

  return (
    <Section
      id="vx-cat-animations"
      title="Speaking Animations"
      description="The animation shown next to your name in voice channels while you talk. Everyone in the channel sees the one you pick; it is saved to your account."
      actions={
        value !== DEFAULT_VOICE_ANIMATION ? (
          <button type="button" className="vx-btn small" onClick={() => choose(DEFAULT_VOICE_ANIMATION)} data-testid="anim-reset">
            Use default ({getVoiceAnimationLabel(DEFAULT_VOICE_ANIMATION)})
          </button>
        ) : null
      }
    >
      <VoiceAnimationStyles />
      <div className="vx-anim-hero" id="vx-setting-speaking-test" data-setting="speaking-test">
        <div className={`vx-anim-hero-stage${previewActive ? ' is-speaking' : ''}`} data-testid="anim-hero" data-active={previewActive ? '1' : '0'}>
          <span className="vx-anim-hero-name" aria-hidden="true">
            <span className="vx-anim-hero-dot" />
            You
          </span>
          <span className="vx-anim-plate vx-anim-hero-slot" style={{ opacity: previewActive ? 1 : 0 }}>
            <AnimationStage type={value} still={still && !test} />
          </span>
        </div>
        <div className="vx-anim-hero-side">
          <div className="vx-anim-hero-title">
            {getVoiceAnimationLabel(value)}
            {saving ? <span className="vx-set-hint"> · saving…</span> : null}
          </div>
          <div className="vx-set-hint" role="status" aria-live="polite">
            {test === 'sim' ? 'Simulating speech: the animation appears while “you” talk, like others will see it.' : test === 'mic' ? (mic.error ? mic.error : mic.db >= gateDb ? 'Others would see you speaking now.' : 'Speak: the animation appears when you are louder than your sensitivity setting.') : 'Pick an animation below; it applies straight away.'}
          </div>
          <div className="vx-inline">
            <button type="button" className={`vx-btn small${test === 'sim' ? ' is-on' : ''}`} aria-pressed={test === 'sim'} onClick={() => setTest((t) => (t === 'sim' ? '' : 'sim'))} data-testid="anim-test-speaking">
              {test === 'sim' ? 'Stop test' : 'Test speaking'}
            </button>
            <button type="button" className={`vx-btn small${test === 'mic' ? ' is-on' : ''}`} aria-pressed={test === 'mic'} onClick={() => setTest((t) => (t === 'mic' ? '' : 'mic'))} data-testid="anim-test-mic">
              {test === 'mic' ? 'Stop microphone' : 'Test with my microphone'}
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="vx-acct-error" role="alert">
          {error}
        </div>
      ) : null}

      {pz.reduceMotion ? (
        <Row
          label="With reduced motion"
          hint={`${motionNote} Speaking indicators still appear and disappear as people talk; choose whether they also move.`}
          settingId="speaking-motion"
          scope="account"
          onReset={() => pz.setThemeValue('speakingMotion', 'follow')}
          isDefault={pz.globalPrefs.theme.speakingMotion !== 'always'}
        >
          <Segmented
            label="Speaking animations with reduced motion"
            value={pz.globalPrefs.theme.speakingMotion}
            options={[
              { value: 'follow', label: 'Still badge' },
              { value: 'always', label: 'Animate anyway' },
            ]}
            onChange={(v) => pz.setThemeValue('speakingMotion', v)}
            testId="anim-motion"
          />
        </Row>
      ) : (
        <div id="vx-setting-speaking-motion" data-setting="speaking-motion" className="vx-set-hint">
          If you turn on Reduce motion (Themes & Accessibility, or your device settings), speaking animations become still badges unless you choose otherwise here.
        </div>
      )}

      <div className="vx-anim-grid" role="radiogroup" aria-label="Speaking animation" id="vx-setting-speaking-animation" data-setting="speaking-animation" data-testid="anim-grid">
        {VOICE_ANIMATION_OPTIONS.map((item) => {
          const on = item.key === value;
          return (
            <button
              key={item.key}
              type="button"
              role="radio"
              aria-checked={on}
              className={`vx-anim-card${on ? ' is-on' : ''}`}
              onClick={() => choose(item.key)}
              onMouseEnter={() => setHovered(item.key)}
              onMouseLeave={() => setHovered((h) => (h === item.key ? '' : h))}
              onFocus={() => setHovered(item.key)}
              onBlur={() => setHovered((h) => (h === item.key ? '' : h))}
              onKeyDown={(e) => {
                const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
                if (!(e.key in keys)) return;
                e.preventDefault();
                const i = VOICE_ANIMATION_OPTIONS.findIndex((x) => x.key === item.key);
                const next = VOICE_ANIMATION_OPTIONS[(i + keys[e.key] + VOICE_ANIMATION_OPTIONS.length) % VOICE_ANIMATION_OPTIONS.length];
                const el = e.currentTarget.parentElement.querySelector(`[data-anim="${next.key}"]`);
                if (el) el.focus();
              }}
              tabIndex={on ? 0 : -1}
              data-anim={item.key}
              data-testid={`anim-card-${item.key}`}
            >
              <span className="vx-anim-card-stage vx-anim-plate">
                {/* Under reduced motion the previews are still, and play while you point at or focus one. */}
                <AnimationStage type={item.key} still={still && hovered !== item.key} />
              </span>
              <span className="vx-anim-card-label">
                {item.label}
                {item.key === DEFAULT_VOICE_ANIMATION ? <span className="vx-anim-card-tag">Default</span> : null}
              </span>
              {saving === item.key ? <span className="vx-set-hint">Saving…</span> : null}
            </button>
          );
        })}
      </div>
    </Section>
  );
}
